import { css } from '@emotion/core';
import {
  COLOR,
  FONT_SIZE,
  FONT_WEIGHT,
  MIN_BUTTON_HEIGHT,
} from '@ordermentum/ui-constants';
import { ButtonProps } from './Button.types';

const BUTTON_SIZES = {
  small: '10rem',
  medium: '12.5rem',
  large: '15rem',
  max: '100%',
  auto: 'auto',
};

const BUTTON_HEIGHTS = {
  normal: MIN_BUTTON_HEIGHT,
  large: '3.5rem',
};

type ButtonWrapperProps = Partial<ButtonProps>;

const ButtonWrapper = (
  props: ButtonWrapperProps = {
    fontSize: 'SM',
    fontWeight: 'MEDIUM',
  }
) => css`
  align-items: center;
  background: ${COLOR.WHITE};
  border: 1px solid ${COLOR.BRAND_500};
  border-radius: ${props.borderRadius || '0'}px;
  color: ${COLOR.BRAND_500};
  cursor: pointer;
  display: flex;
  font-weight: ${FONT_WEIGHT[props.fontWeight || 'MEDIUM']};
  font-size: ${FONT_SIZE[props.fontSize || 'SM']};
  justify-content: center;
  min-height: ${BUTTON_HEIGHTS[props.height || 'normal']};
  padding: 0.33rem 1rem;
  width: ${BUTTON_SIZES[props.size || 'auto']};

  /* Nested Font Icon */
  & [class*='om-icon-']::before {
    /* enforce brand color */
    color: ${COLOR.BRAND_500} !important;
    /* all icons inside button need a margin */
    margin-right: 0.5rem;
  }
  /* Nested SVG Icon */
  & [data-id='icon'] {
    /* all icons inside button need a margin */
    margin-right: 0.5rem;
  }

  &:hover {
    box-shadow: 0 0 0 1px ${COLOR.BRAND_500};
  }

  &:focus {
    outline: ${COLOR.GRAY_400} auto 5px;
    outline-offset: -2px;
  }

  &[disabled],
  &:disabled {
    background: ${COLOR.WHITE};
    border-color: ${COLOR.GRAY_300};
    box-shadow: none;
    color: ${COLOR.GRAY_400};
    cursor: not-allowed;

    &:hover {
      border-color: ${COLOR.GRAY_300};
      box-shadow: none;
    }

    /* Nested Font Icon, when inactive */
    & [class*='om-icon-']::before {
      /* enforce inactive color */
      color: ${COLOR.GRAY_400} !important;
    }
  }
`;

const HiddenButton = css`
  display: none;
`;

const PrimaryButton = css`
  background: ${COLOR.BRAND_500} !important;
  color: ${COLOR.WHITE} !important;

  /* Nested Font Icon */
  & [class*='om-icon-']::before {
    /* enforce white color */
    color: ${COLOR.WHITE} !important;
  }
`;

const CancelButton = css`
  background: none;
  border: none;
  color: ${COLOR.GRAY_400};
  font-weight: ${FONT_WEIGHT.MEDIUM};

  &:disabled {
    border: none;
  }

  &:hover {
    border: none;
    box-shadow: none;
    text-decoration: underline;
  }

  &:disabled:hover {
    text-decoration: none;
  }
`;

const BackDeleteButton = css`
  background: none;
  border: 1px solid ${COLOR.GRAY_400};
  color: ${COLOR.GRAY_400};

  /* Nested Font Icon */
  & [class*='om-icon-']::before {
    /* enforce gray color */
    color: ${COLOR.GRAY_400} !important;
  }

  &:hover {
    box-shadow: 0 0 0 1px ${COLOR.GRAY_300};
  }
`;

const ResetButton = css`
  border: none;
  background: none;
  padding: 0;
  &:hover {
    box-shadow: none;
  }
`;

export {
  ButtonWrapper,
  HiddenButton,
  PrimaryButton,
  CancelButton,
  BackDeleteButton,
  ResetButton,
};
